/**
 * src/lib/boothTheme.js — which costume the booth wears, decided by the event.
 * ═══════════════════════════════════════════════════════════════════════════
 * The RenFest booth is festival-tested and is NOT rewritten for a second look
 * (CLAUDE.md R-15). Instead: every RenFest constant stays exactly where it is,
 * byte for byte, and this file only supplies OVERRIDES for events that are not
 * RenFest. `THEME` is null on the RenFest route, and every call site reads
 * `THEME?.x || <the original value>`, so a null theme is the booth as it was.
 *
 * ⚠️ READ ONCE, AT MODULE LOAD, FROM THE PATH. A booth is reached by scanning a
 * QR code straight onto /:tenant/:event, and nothing inside the booth ever
 * navigates to another event (a route change would kill a running portrait,
 * see RenFestSelfiePage). So the path at load IS the event for the whole visit.
 *
 * ⚠️ THE PATH PICKS THE COSTUME AND NOTHING ELSE. Spend, caps and the run row
 * are scoped by the booth token on the server (D-02). A guest who edits the URL
 * gets a different skin, not different permissions.
 *
 * ⚠️ THE SCENE KEYS ARE THE CONTRACT WITH THE GENERATOR. Every retro key
 * starts `retro_`, and `renfest-selfie-background.js` switches its whole prompt
 * (style, wardrobe, banner) on that prefix. Add a scene here and it MUST exist
 * in RETRO_SCENES and RETRO_PLACES there, or the guest gets the fallback scene.
 *
 * Added 2026-10-03 for "Retro Night - Nelly's Birthday Bash" (70s / 80s).
 * ═══════════════════════════════════════════════════════════════════════════
 */

/* ⚠️ NO BACKTICKS INSIDE THIS CSS. It is a template literal, same trap as
   BoothStyles in boothChrome.jsx.

   ⚠️ EVERY RULE IS PREFIXED `.rf-retro`. The booth's own rules live in two
   <style> blocks (boothChrome and RenFestSelfie) and the second one mounts
   AFTER this, so source order cannot win. One extra class of specificity can,
   and it means these rules do nothing at all unless the page root carries
   `rf-retro` — which only RenFestSelfiePage adds, and only for this theme.

   ⚠️ COLOURS AND FONTS ONLY. Nothing here touches display, height, flex or
   overflow: the layout chain in RenFestSelfie is load-bearing (R-31). */
const RETRO_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Righteous&display=swap');

  .rf-retro .rf-ink, .rf-retro .rf-label, .rf-retro .rf-chip, .rf-retro .rf-big,
  .rf-retro .rf-pick, .rf-retro .rf-home, .rf-retro .rf-tag,
  .rf-retro .rf-scene-name, .rf-retro .rf-count {
    font-family: 'Righteous', 'Futura', 'Avenir Next Condensed', 'Arial Black', system-ui, sans-serif;
    letter-spacing: .02em;
  }
  .rf-retro .rf-input {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    background: rgba(10,0,40,.55); color: #ffffff;
    border: 1px solid rgba(0,229,255,.45);
  }
  .rf-retro .rf-pick, .rf-retro .rf-chip, .rf-retro .rf-home, .rf-retro .rf-scene {
    background: linear-gradient(180deg, #6a22c8, #2c0b6e);
    border: 1px solid rgba(255,61,220,.6);
    color: #ffffff;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.2), 0 0 14px rgba(255,61,220,.28), 0 3px 0 rgba(0,0,0,.4);
  }
  .rf-retro .rf-big, .rf-retro .rf-chip.is-on, .rf-retro .rf-home.is-accent,
  .rf-retro .rf-tag, .rf-retro .rf-scene.is-on {
    background: linear-gradient(180deg, #ff4fd8 0%, #ff7a1a 62%, #ffd21a 100%);
    color: #1c0038;
    border: 1px solid #ff2bd6;
    box-shadow: inset 0 2px 0 rgba(255,255,255,.5), 0 4px 0 rgba(0,0,0,.4), 0 0 22px rgba(255,61,220,.5);
  }
  .rf-retro .rf-step-title {
    text-transform: uppercase;
    text-shadow: 0 3px 0 #ff2bd6, 0 0 24px rgba(255,61,220,.65);
  }
  .rf-retro .rf-step-sub { color: rgba(255,255,255,.82); }
  .rf-retro .rf-dot.is-on { box-shadow: 0 0 10px rgba(255,61,220,.9); }
  .rf-retro .rf-count {
    background: linear-gradient(180deg, #ffffff 0%, #ffe94a 38%, #ff7a1a 72%, #ff2bd6 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
    filter: drop-shadow(0 4px 0 #3a0a7a) drop-shadow(0 0 26px rgba(255,61,220,.85));
  }
  .rf-retro .rf-wood {
    background: linear-gradient(180deg, #3a1280, #1d0750);
    border: 1px solid rgba(0,229,255,.4);
  }
`;

const RETRO = {
  key: 'retro',
  rootClass: 'rf-retro',
  css: RETRO_CSS,

  /* Replaces GOLD everywhere it is read. Poster yellow, straight off the flyer:
     it is the one colour on that artwork that stays readable on purple. */
  accent: '#ffe94a',
  pageBg:
    'radial-gradient(90% 55% at 50% 0%, rgba(255,61,220,.50), transparent 70%), '
    + 'radial-gradient(70% 40% at 50% 100%, rgba(0,229,255,.22), transparent 70%), '
    + 'linear-gradient(180deg, #2c0b6e 0%, #12042e 100%)',

  /* ⚠️ NO SHOPPE, AND NOT AS A TASTE CHOICE. The Shoppe's shelf is hardwired to
     the RenFest event (EVENT_SLUG in shoppeCatalogue.js) and its pickup point
     is booth #383. Showing it at a birthday party would sell products for
     collection at a festival booth across town. */
  shop: false,
  /* The Selfie ID exists so a guest can say six characters to a staffer at the
     print counter. This party has no counter, so the line "say this at the
     booth" would send people looking for one. */
  claimCode: false,
  /* Every portrait at this party is the cartoon. Naming a likeness here removes
     the style step from the wizard; see FORCED_STYLE in RenFestSelfie.jsx.
     `full` is the value the booth labels "Cartoony". */
  likeness: 'full',

  welcome: {
    title: 'Free Retro Portrait',
    sub: 'Your 70s and 80s glow-up',
    line: "Retro Night · Nelly's Birthday Bash",
  },
  shareTitle: "Retro Night - Nelly's Birthday Bash",
  detailPlaceholder: 'e.g. gold chains and a huge perm',
  prepLine: 'DJ Airbrush is shaking the spray cans',
  painter: { name: 'DJ Airbrush', wait: 'about a minute' },

  /* ⚠️ `k` MUST START `retro_` — see the header. Emoji are all Unicode 13 or
     older so a three-year-old phone still draws them. */
  scenes: [
    { k: 'retro_disco',    e: '\u{1F57A}', n: 'Disco Dance Floor' },
    { k: 'retro_roller',   e: '\u{1F6FC}', n: 'Roller Rink' },
    { k: 'retro_arcade',   e: '\u{1F579}\u{FE0F}', n: 'The Arcade' },
    { k: 'retro_boombox',  e: '\u{1F4FB}', n: 'Boombox Block Party' },
    { k: 'retro_neon',     e: '\u{1F334}', n: 'Neon Beach Nights' },
    { k: 'retro_rock',     e: '\u{1F3B8}', n: 'Rock Star' },
    { k: 'retro_funk',     e: '\u{1F3A4}', n: 'Funk TV Stage' },
    { k: 'retro_aerobics', e: '\u{1F4AA}', n: 'Aerobics Video' },
    { k: 'retro_prom',     e: '\u{1F483}', n: '80s Prom Photo' },
    { k: 'retro_album',    e: '\u{1F4BF}', n: 'Album Cover' },
  ],

  patter: [
    'SHAKING THE SPRAY CANS', 'FAR OUT! WHAT A FACE!', 'TEASING THE HAIR HIGHER',
    'OH, THAT WAS A GOOD ONE', 'MORE HAIRSPRAY. MORE.', 'POLISHING THE DISCO BALL',
    'MIXING HOT PINK AND ELECTRIC BLUE', 'TOTALLY TUBULAR TAKE',
    'AIRBRUSHING THE EYES - DELICATE WORK', 'REWINDING THE TAPE',
    'ADDING SHOULDER PADS', 'THE LIKENESS IS RADICAL',
    'CAN YOU DIG IT?', 'SOMEONE FLIP THE RECORD',
    'FEATHERING THE HAIR, STRAND BY STRAND', 'THAT JAWLINE IS DYNAMITE',
    'HE STEPS BACK. HE SQUINTS. HE NODS.', 'CRANKING UP THE NEON',
    'WARMING THE SKIN TONES', 'NELLY IS GOING TO LOVE THIS',
    'SEQUINS TAKE PATIENCE', 'LASERS: ON',
    'A LITTLE MORE GLITTER', 'SIGNING THE CORNER',
  ],
  overtimePatter: [
    'FUSSING OVER ONE LAST SPARKLE', 'ONE MORE COAT OF GLOSS, PROMISE',
    'YOU CANNOT RUSH FUNK', 'ALMOST YOURS, SUPERSTAR',
  ],
};

/* ⚠️ EVENT SLUGS, NOT TENANT SLUGS. One tenant can run a festival and a
   birthday party in the same week. Add a slug here to give another event the
   retro costume; the generator needs nothing extra, it follows the scene keys. */
const THEMES_BY_EVENT = {
  'retro-night': RETRO,
};

function themeFromPath() {
  try {
    const [, , eventSlug] = window.location.pathname.split('/');
    return THEMES_BY_EVENT[String(eventSlug || '').toLowerCase()] || null;
  } catch {
    return null;
  }
}

/** null on RenFest and on any event with no entry above. */
export const THEME = themeFromPath();

export default THEME;
